import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Wrap,
  WrapItem,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  Spinner,
  Alert,
  AlertIcon,
  Icon,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { FaSearch, FaVial, FaFlask, FaInfoCircle } from 'react-icons/fa';

/**
 * LabAide collection-requirements lookup.
 *
 * Phlebotomist-facing quick reference: "for this test, what tube / specimen /
 * volume do I collect?" Calls our server-side proxy (api/labaide-query.js),
 * which holds the LabAide API key — the key is NEVER in the browser.
 *
 * Handles LabAide's swab-source clarification flow: some tests (e.g. GC/
 * chlamydia) need a collection site before they can be answered, signalled by
 * `requiresSourceSelection`. We then show the five site choices and re-query.
 *
 * COMPLIANCE: this searches a test name/code only — never enter patient details.
 */

const SWAB_SOURCES = ['throat', 'vaginal', 'rectal', 'wound', 'urine'];

// Render a bullet line, honoring **bold** segments from LabAide's prose.
function renderBullet(line) {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Text as="span" key={i} fontWeight="bold">
          {part.slice(2, -2)}
        </Text>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

// Stable-ish per-mount session id so related calls (e.g. clarification round
// trips) are grouped together in LabAide's telemetry.
function makeSessionId() {
  return `phleb-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

const LabAideLookup = () => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { bullets, requiresSourceSelection, responseType }
  const [lastQuery, setLastQuery] = useState(''); // the text that produced `result`
  const sessionIdRef = useRef(makeSessionId());

  const apiBase = process.env.REACT_APP_API_URL || '';

  const runQuery = async (queryText, swabSource) => {
    const trimmed = (queryText || '').trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${apiBase}/api/labaide-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: trimmed,
          session_id: sessionIdRef.current,
          ...(swabSource ? { swab_source: swabSource } : {}),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.ok === false) {
        setResult(null);
        if (res.status === 429) {
          const secs = data.retryAfterSeconds ? ` (about ${data.retryAfterSeconds}s)` : '';
          setError(`Too many lookups right now — please wait a moment${secs} and try again.`);
        } else {
          setError(data.error || 'Lookup failed. Please try again.');
        }
        return;
      }

      setLastQuery(trimmed);
      setResult({
        bullets: data.bullets || [],
        requiresSourceSelection: !!data.requiresSourceSelection,
        responseType: data.responseType || null,
      });
    } catch (err) {
      setResult(null);
      setError('Could not reach the lookup service. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runQuery(text);
  };

  const handleSwabSource = (source) => {
    // Re-send the SAME query text, now with the chosen collection site.
    runQuery(lastQuery || text, source);
  };

  const needsSource = result?.requiresSourceSelection;

  return (
    <Container maxW="container.md" py={{ base: 6, md: 10 }}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <HStack spacing={3} mb={1}>
            <Icon as={FaVial} color="blue.500" boxSize={6} />
            <Heading size="lg">Test Collection Lookup</Heading>
          </HStack>
          <Text color="gray.600">
            Look up the tube, specimen type, and minimum volume for any lab test.
            Search by test name or code — for example <b>CBC</b>, <b>vitamin d</b>,
            or <b>L041</b>.
          </Text>
        </Box>

        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          Search the test name or code only. Do not enter patient names, dates of
          birth, MRNs, or any other patient details. This is decision support — the
          lab's official requirements remain the source of truth.
        </Alert>

        <form onSubmit={handleSubmit}>
          <HStack spacing={3}>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Enter a test name or code…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={500}
                bg="white"
              />
            </InputGroup>
            <Button
              type="submit"
              size="lg"
              colorScheme="blue"
              px={8}
              isLoading={loading}
              loadingText="Searching"
              isDisabled={!text.trim()}
            >
              Search
            </Button>
          </HStack>
        </form>

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading && !result && (
          <HStack justify="center" py={10} color="gray.500">
            <Spinner size="md" />
            <Text>Looking up collection requirements…</Text>
          </HStack>
        )}

        {result && (
          <Card variant="outline" borderColor={needsSource ? 'orange.200' : 'gray.200'}>
            <CardBody>
              {needsSource ? (
                <VStack align="stretch" spacing={4}>
                  <HStack>
                    <Icon as={FaInfoCircle} color="orange.500" />
                    <Heading size="sm">Which collection site?</Heading>
                  </HStack>
                  <List spacing={2}>
                    {result.bullets.map((line, i) => (
                      <ListItem key={i} color="gray.700">
                        {renderBullet(line)}
                      </ListItem>
                    ))}
                  </List>
                  <Divider />
                  <Text fontSize="sm" color="gray.600">
                    Choose the collection site to get the exact tube and specimen:
                  </Text>
                  <Wrap spacing={3}>
                    {SWAB_SOURCES.map((source) => (
                      <WrapItem key={source}>
                        <Button
                          colorScheme="orange"
                          variant="outline"
                          textTransform="capitalize"
                          isDisabled={loading}
                          onClick={() => handleSwabSource(source)}
                        >
                          {source}
                        </Button>
                      </WrapItem>
                    ))}
                  </Wrap>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between">
                    <HStack>
                      <Icon as={FaFlask} color="blue.500" />
                      <Heading size="sm">Collection Requirements</Heading>
                    </HStack>
                    {result.responseType && (
                      <Badge colorScheme="blue" textTransform="none">
                        {result.responseType === 'panel_info' ? 'Panel' : 'Test'}
                      </Badge>
                    )}
                  </HStack>
                  <List spacing={3}>
                    {result.bullets.map((line, i) => (
                      <ListItem key={i} display="flex" alignItems="flex-start">
                        <ListIcon as={FaVial} color="blue.400" mt={1} />
                        <Box>{renderBullet(line)}</Box>
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              )}
            </CardBody>
          </Card>
        )}
      </VStack>
    </Container>
  );
};

export default LabAideLookup;
